/**
 * Pure z-order reachability checks over a unified (shapes + text boxes) layer order. Shared by the
 * Arrange panel (`layout-panel.tsx`, `select-tool/select-tool.tsx`) and the object context menu
 * (`center-canvas/components/canvas-context-menus.tsx`) so both surfaces agree on when an arrange
 * action would actually change stacking order.
 */

/** True when at least one non-selected id sits above the topmost selected id. */
export function canBringSelectionForward(selectedIds: string[], unifiedOrder: string[]): boolean {
  if (selectedIds.length === 0 || unifiedOrder.length <= 1) return false

  const selected = new Set(selectedIds)
  let highestSelectedIndex = -1

  for (let i = 0; i < unifiedOrder.length; i++) {
    if (selected.has(unifiedOrder[i]!)) highestSelectedIndex = i
  }

  for (let i = highestSelectedIndex + 1; i < unifiedOrder.length; i++) {
    if (!selected.has(unifiedOrder[i]!)) return true
  }

  return false
}

/** True when at least one non-selected id sits below the lowest selected id. */
export function canSendSelectionBackward(selectedIds: string[], unifiedOrder: string[]): boolean {
  if (selectedIds.length === 0 || unifiedOrder.length <= 1) return false

  const selected = new Set(selectedIds)
  let lowestSelectedIndex = unifiedOrder.length

  for (let i = 0; i < unifiedOrder.length; i++) {
    if (selected.has(unifiedOrder[i]!) && i < lowestSelectedIndex) lowestSelectedIndex = i
  }

  for (let i = lowestSelectedIndex - 1; i >= 0; i--) {
    if (!selected.has(unifiedOrder[i]!)) return true
  }

  return false
}

/**
 * The ids in `unifiedOrder` that are still on the canvas. The layer order keeps the id of a deleted
 * object (undo of a delete relies on it keeping its slot), and one left below the bottom-most object
 * made Send backward look possible on it (WMPCM-12998). Every arrange surface reads availability
 * through this. Without a shape list (no manager yet) the order is returned as-is.
 */
export function liveLayerOrder(
  unifiedOrder: string[],
  shapes: readonly { id: string }[] | undefined
): string[] {
  if (!shapes) return unifiedOrder

  const liveIds = new Set(shapes.map((shape) => shape.id))

  return unifiedOrder.filter((id) => liveIds.has(id))
}

/**
 * Puts a reordered `liveOrder` back into `prev`: ids missing from `liveOrder` (deleted objects)
 * keep their indices, and the live ids fill the other slots in their new order. Lets an arrange
 * step over deleted ids, so one Send backward always moves the object behind a visible one.
 */
export function mergeLiveLayerOrder(
  prev: readonly string[],
  liveOrder: readonly string[]
): string[] {
  const liveIds = new Set(liveOrder)
  let next = 0

  return prev.map((id) => (liveIds.has(id) ? liveOrder[next++]! : id))
}

/**
 * Moves `expandedIds` to the top of `prev`, using `orderedIds` as the exact relative order for
 * that block rather than whatever relative order they already happen to be in.
 *
 * Used for freshly created content (e.g. clipboard paste) where each id was appended to the
 * unified layer order individually as it was created — often not in the original relative
 * stacking order (a pasted group's children may need to be created before the group itself, for
 * persistence reasons unrelated to z-order). `orderedIds` should already be `expandedIds` in the
 * desired order; any id present in `expandedIds` but missing from `orderedIds` is appended last
 * (defensive — real callers pass a fully-expanded `orderedIds`, so this shouldn't trigger).
 */
export function insertOrderedIntoUnifiedLayer(
  prev: readonly string[],
  orderedIds: readonly string[],
  expandedIds: ReadonlySet<string>
): string[] {
  const explicitOrder = orderedIds.filter((id) => expandedIds.has(id))
  const explicitOrderSet = new Set(explicitOrder)
  const missingExpanded = [...expandedIds].filter((id) => !explicitOrderSet.has(id))

  return [...prev.filter((id) => !expandedIds.has(id)), ...explicitOrder, ...missingExpanded]
}
